// src/constants/findings.js

/**
 * The structured investigation record for an incident.
 *
 * Chat explains an incident to the other person in the room; findings explain it
 * to everyone who comes afterwards — the customer reading the report, the next
 * expert picking up a reopened incident, and eventually the resolution
 * intelligence database looking for patterns across a thousand incidents.
 *
 * `id` values are persisted in Firestore and must stay stable.
 */

export const FINDING_KIND = {
  HYPOTHESIS: "hypothesis",
  ACTION: "action",
  OBSERVATION: "observation",
  ROOT_CAUSE: "root_cause",
  FIX: "fix",
};

/** Hypotheses are the only kind that carries a verdict. */
export const FINDING_STATUS = {
  OPEN: "open",
  CONFIRMED: "confirmed",
  RULED_OUT: "ruled_out",
};

export const FINDING_KINDS = {
  [FINDING_KIND.HYPOTHESIS]: {
    id: FINDING_KIND.HYPOTHESIS,
    label: "Hypothesis",
    shortLabel: "Hypothesis",
    // Shown in the composer to make the expected shape of the entry obvious.
    placeholder:
      "What might be causing this? e.g. the webhook secret was not promoted with the deploy",
    icon: "Lightbulb",
    accentClass: "text-amber-600",
    chipClass: "bg-amber-50 text-amber-700 border-amber-200",
    expertOnly: true,
    verdictable: true,
  },
  [FINDING_KIND.ACTION]: {
    id: FINDING_KIND.ACTION,
    label: "Action taken",
    shortLabel: "Action",
    placeholder:
      "What did you do? e.g. compared the signing secret in Vercel against the Stripe endpoint",
    icon: "Wrench",
    accentClass: "text-blue-600",
    chipClass: "bg-blue-50 text-blue-700 border-blue-200",
    expertOnly: true,
    verdictable: false,
  },
  [FINDING_KIND.OBSERVATION]: {
    id: FINDING_KIND.OBSERVATION,
    label: "Observation",
    shortLabel: "Observation",
    placeholder:
      "What did you see? e.g. all deliveries since 23:41 return 401, earlier ones return 200",
    icon: "Eye",
    accentClass: "text-slate-600",
    chipClass: "bg-slate-100 text-slate-700 border-slate-200",
    expertOnly: false,
    verdictable: false,
  },
  [FINDING_KIND.ROOT_CAUSE]: {
    id: FINDING_KIND.ROOT_CAUSE,
    label: "Root cause",
    shortLabel: "Root cause",
    placeholder:
      "Why did this happen? The underlying cause, not the symptom the customer reported",
    icon: "Crosshair",
    accentClass: "text-rose-600",
    chipClass: "bg-rose-50 text-rose-700 border-rose-200",
    expertOnly: true,
    verdictable: false,
  },
  [FINDING_KIND.FIX]: {
    id: FINDING_KIND.FIX,
    label: "Fix applied",
    shortLabel: "Fix",
    placeholder:
      "What resolved it? Written so the customer could repeat it themselves",
    icon: "CheckCircle2",
    accentClass: "text-emerald-600",
    chipClass: "bg-emerald-50 text-emerald-700 border-emerald-200",
    expertOnly: true,
    verdictable: false,
  },
};

/** Composer order: how an investigation actually progresses. */
export const FINDING_KIND_ORDER = [
  FINDING_KIND.HYPOTHESIS,
  FINDING_KIND.ACTION,
  FINDING_KIND.OBSERVATION,
  FINDING_KIND.ROOT_CAUSE,
  FINDING_KIND.FIX,
];

export const FINDING_STATUS_LABELS = {
  [FINDING_STATUS.OPEN]: "Investigating",
  [FINDING_STATUS.CONFIRMED]: "Confirmed",
  [FINDING_STATUS.RULED_OUT]: "Ruled out",
};

export function getFindingKind(kindId) {
  return FINDING_KINDS[kindId] || FINDING_KINDS[FINDING_KIND.OBSERVATION];
}

/** Kinds the given viewer is allowed to author. */
export function authorableKinds(viewerRole) {
  const isExpert = viewerRole === "expert";
  return FINDING_KIND_ORDER.filter(
    (id) => isExpert || !FINDING_KINDS[id].expertOnly
  );
}

/**
 * The two entries that answer "what was wrong and what fixed it".
 *
 * The resolution report and the intelligence export both need these, and both
 * should read the latest of each rather than assuming there is only ever one.
 */
export function extractResolution(findings = []) {
  const latestOf = (kind) =>
    findings.filter((f) => f.kind === kind).slice(-1)[0] || null;

  return {
    rootCause: latestOf(FINDING_KIND.ROOT_CAUSE),
    fix: latestOf(FINDING_KIND.FIX),
  };
}

/** True once the incident has a documented cause and fix. */
export function hasResolutionFindings(findings = []) {
  const { rootCause, fix } = extractResolution(findings);
  return Boolean(rootCause && fix);
}
